import IOnGetOrdersPickListDTO from "../models/DTO/IOnGetOrdersPickListDTO";
import IOrdersDetailDTO from "../models/DTO/IOrdersDetailDTO";
import IOrder_Item_PickListDTO from "../models/DTO/IOrder_Item_PickListDTO";
import IPickListOrderItemQtyMap from "../models/DTO/IPickListOrderItemQtyMap";

/**
 * Pure helpers that pivot the Simple Pick List socket payload into a
 * customer (order) x catalog item matrix. No React, no side effects.
 */

export interface IPickListMatrixColumn {
    orderGuid: string;
    orderName: string;
    vendorName: string;
    contactName: string;
    phone: string;
}

export interface IPickListMatrixRow {
    item: IOrder_Item_PickListDTO;
    // cells[i] lines up with columns[i]; null means "blank" (the order has none of this item).
    cells: (number | null)[];
    rowTotal: number;
}

export interface IPickListMatrixGroup {
    // null when the matrix is not grouped by root category (no header / subtotal rows).
    name: string | null;
    rows: IPickListMatrixRow[];
    columnTotals: number[];
    groupTotal: number;
}

export interface IPickListMatrix {
    columns: IPickListMatrixColumn[];
    groups: IPickListMatrixGroup[];
    columnTotals: number[];
    grandTotal: number;
}

const buildMatrixColumns = (orders: IOrdersDetailDTO[]): IPickListMatrixColumn[] => {
    return orders.map((order) => {
        return {
            orderGuid: order.guid,
            orderName: order.name || "",
            vendorName: order.vendor || "",
            contactName: order.Ship_To?.name || "",
            phone: order.Ship_To?.phone || order.Bill_To?.phone || "",
        };
    });
};

const getColumnTitle = (column: IPickListMatrixColumn): string => {
    return column.vendorName || column.orderName;
};

// Single-line header text for CSV export: "Vendor / Contact / Phone", skipping empty parts.
const formatColumnHeaderText = (column: IPickListMatrixColumn): string => {
    return [getColumnTitle(column), column.contactName, column.phone].filter((part) => part.trim().length > 0).join(" / ");
};

const getCellQty = (
    qtyMap: IPickListOrderItemQtyMap | undefined,
    orderGuid: string,
    catalogItemId: number,
    includeBonus: boolean
): number | null => {
    const entry = qtyMap?.[orderGuid]?.[catalogItemId];

    if (!entry) {
        return null;
    }

    let qty = Number(entry.total_qty) || 0;

    if (includeBonus) {
        qty += Number(entry.total_bonus_qty_frozen) || 0;
    }

    return qty === 0 ? null : qty;
};

const buildMatrixRow = (
    item: IOrder_Item_PickListDTO,
    columns: IPickListMatrixColumn[],
    qtyMap: IPickListOrderItemQtyMap | undefined,
    includeBonus: boolean
): IPickListMatrixRow => {
    const cells = columns.map((column) => getCellQty(qtyMap, column.orderGuid, item.catalog_item_id, includeBonus));
    const rowTotal = cells.reduce<number>((sum, cell) => sum + (cell || 0), 0);

    return { item, cells, rowTotal };
};

const sumColumns = (rows: IPickListMatrixRow[], columnCount: number): number[] => {
    const totals: number[] = new Array(columnCount).fill(0);

    for (const row of rows) {
        row.cells.forEach((cell, index) => {
            totals[index] += cell || 0;
        });
    }

    return totals;
};

const sum = (values: number[]): number => {
    return values.reduce((total, value) => total + value, 0);
};

/**
 * @param data          The pick list payload (orders drive the columns, orderItemQtyMap drives the cells).
 * @param itemGroups    Ordered item groups. Use a single entry keyed by null when not grouping by root category.
 * @param includeBonus  Add bonus_qty_frozen to each cell (supplier has_order_bonus), matching the list layout.
 */
const buildPickListMatrix = (
    data: IOnGetOrdersPickListDTO,
    itemGroups: Map<string | null, IOrder_Item_PickListDTO[]>,
    includeBonus: boolean
): IPickListMatrix => {
    const columns = buildMatrixColumns(data.orders || []);
    const groups: IPickListMatrixGroup[] = [];

    for (const [name, items] of itemGroups) {
        const rows = items.map((item) => buildMatrixRow(item, columns, data.orderItemQtyMap, includeBonus));
        const columnTotals = sumColumns(rows, columns.length);

        groups.push({ name, rows, columnTotals, groupTotal: sum(columnTotals) });
    }

    const columnTotals = columns.map((_column, index) => {
        return groups.reduce((total, group) => total + group.columnTotals[index], 0);
    });

    return { columns, groups, columnTotals, grandTotal: sum(columnTotals) };
};

// The matrix always needs at least one label column so rows stay identifiable.
const getMatrixLabelColCount = (showSku: boolean, showItemName: boolean): number => {
    return Math.max(1, (showSku ? 1 : 0) + (showItemName ? 1 : 0));
};

const PickListMatrixService = {
    buildMatrixColumns,
    getColumnTitle,
    formatColumnHeaderText,
    getCellQty,
    buildPickListMatrix,
    getMatrixLabelColCount,
};

export default PickListMatrixService;
